import React, { useEffect, useMemo, useRef, useState } from "react";
import { Map as MaplibreMap, NavigationControl, LngLatBounds } from "maplibre-gl";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { ScatterplotLayer, PathLayer, TextLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import "maplibre-gl/dist/maplibre-gl.css";

// Raster OSM style — MapLibre with no vector-tile key or bill
const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://a.tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};

const STATUS_COLOR = {
  green: [22, 163, 74],
  yellow: [234, 179, 8],
  blue: [37, 99, 235],
  red: [220, 38, 38],
};

export const TerritoryMap = ({ doctors = [], heatmap = null, route = null, height = "30rem" }) => {
  const container = useRef(null);
  const map = useRef(null);
  const overlay = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const points = useMemo(() => doctors.filter((d) => d.latitude && d.longitude), [doctors]);

  useEffect(() => {
    if (!container.current || map.current) return;
    map.current = new MaplibreMap({
      container: container.current,
      style: OSM_STYLE,
      center: [28.2293, -25.7479],
      zoom: 8,
      attributionControl: { compact: true },
    });
    map.current.addControl(new NavigationControl({ showCompass: false }), "top-left");
    overlay.current = new MapboxOverlay({ interleaved: false, layers: [] });
    map.current.addControl(overlay.current);
    return () => {
      map.current?.remove();
      map.current = null;
      overlay.current = null;
    };
  }, []);

  useEffect(() => {
    if (!overlay.current) return;
    const layers = [];

    if (heatmap?.points?.length) {
      layers.push(
        new HeatmapLayer({
          id: "visit-frequency",
          data: heatmap.points,
          getPosition: (d) => [d.longitude, d.latitude],
          getWeight: (d) => d.weight,
          radiusPixels: 70,
          intensity: 1,
          threshold: 0.03,
          // Red where a rep barely goes, green where they spend their time
          colorRange: [
            [254, 224, 210],
            [252, 146, 114],
            [239, 68, 68],
            [250, 204, 21],
            [134, 239, 172],
            [22, 163, 74],
          ],
        })
      );
    }

    if (heatmap?.gaps?.length) {
      layers.push(
        new ScatterplotLayer({
          id: "coverage-gaps",
          data: heatmap.gaps,
          getPosition: (d) => [d.longitude, d.latitude],
          getRadius: 9,
          radiusUnits: "pixels",
          getFillColor: [220, 38, 38, 140],
          getLineColor: [153, 27, 27],
          lineWidthMinPixels: 1,
          stroked: true,
          pickable: true,
          onHover: ({ object, x, y }) =>
            setTooltip(object ? { x, y, text: `${object.doctor_name} — no contact in this window` } : null),
        })
      );
    }

    layers.push(
      new ScatterplotLayer({
        id: "doctors",
        data: points,
        getPosition: (d) => [d.longitude, d.latitude],
        getRadius: (d) => (d.tier === "VIP" ? 10 : 7),
        radiusUnits: "pixels",
        getFillColor: (d) => [...(STATUS_COLOR[d.status] || STATUS_COLOR.red), 230],
        getLineColor: [255, 255, 255],
        lineWidthMinPixels: 1.5,
        stroked: true,
        pickable: true,
        onHover: ({ object, x, y }) =>
          setTooltip(
            object
              ? {
                  x,
                  y,
                  text: `${object.doctor_name} · ${object.tier} · ${object.referrals_90days} referrals / ${
                    object.visits_90days + object.appointments_90days
                  } contacts in 90d`,
                }
              : null
          ),
      })
    );

    if (route?.optimized_route?.length) {
      const stops = route.optimized_route.filter((s) => s.latitude && s.longitude);
      const path = route.geometry?.coordinates || stops.map((s) => [s.longitude, s.latitude]);
      layers.push(
        new PathLayer({
          id: "route-line",
          data: [{ path }],
          getPath: (d) => d.path,
          getColor: [37, 99, 235, 200],
          getWidth: 4,
          widthUnits: "pixels",
        }),
        new TextLayer({
          id: "route-order",
          data: stops,
          getPosition: (d) => [d.longitude, d.latitude],
          getText: (d) => String(d.stop_number),
          getSize: 14,
          getColor: [255, 255, 255],
          background: true,
          getBackgroundColor: [30, 64, 175, 235],
          backgroundPadding: [6, 4],
          fontWeight: 700,
        })
      );
    }

    overlay.current.setProps({ layers });

    const all = [...points, ...(heatmap?.points || []), ...(heatmap?.gaps || [])].filter(
      (d) => d.latitude && d.longitude
    );
    if (map.current && all.length) {
      const bounds = all.reduce(
        (acc, d) => acc.extend([d.longitude, d.latitude]),
        new LngLatBounds([all[0].longitude, all[0].latitude], [all[0].longitude, all[0].latitude])
      );
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 500 });
    }
  }, [points, heatmap, route]);

  return (
    <div className="relative">
      <div ref={container} style={{ height }} className="w-full rounded-lg border" data-testid="territory-map" />
      {tooltip && (
        <div
          className="absolute z-10 px-2 py-1 rounded bg-slate-900 text-white text-xs pointer-events-none"
          style={{ left: tooltip.x + 8, top: tooltip.y + 8 }}
        >
          {tooltip.text}
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground" data-testid="map-legend">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ background: "#16a34a" }} />
          Visited + referring
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ background: "#eab308" }} />
          Referring, not visited
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ background: "#2563eb" }} />
          Visited, not referring
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ background: "#dc2626" }} />
          Inactive
        </span>
        <span>Bigger pin = VIP doctor</span>
      </div>
    </div>
  );
};

export default TerritoryMap;
